import {Component, computed, effect, inject, input, model, output, untracked} from '@angular/core';
import {Dialog} from 'primeng/dialog';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Button} from 'primeng/button';
import {
  CreateNewInvoiceRequestParams,
  InvoiceStatus,
  ProviderContractInvoiceResponse,
  ProviderInvoiceType,
  AdminProviderContractControllerService,
} from '@frontend/shared-api';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {DirectusService, DirectusTranslatedItem} from '@frontend/directus';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormFieldComponent} from '../../../../../shared/components/inputs/form-field/form-field.component';
import {FormFieldSelectComponent} from '../../../../../shared/components/inputs/form-field/form-field-select/form-field-select.component';
import {filter, finalize, map, switchMap} from 'rxjs';

@Component({
  selector: 'app-provider-invoice-upload-dialog',
  imports: [
    Dialog,
    TranslocoPipe,
    Button,
    FormFieldComponent,
    FormFieldSelectComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './provider-invoice-upload-dialog.component.html',
})
export class ProviderInvoiceUploadDialogComponent {
  private readonly directusService = inject(DirectusService);
  private readonly transloco = inject(TranslocoService);
  private readonly adminProviderContractController = inject(AdminProviderContractControllerService);
  private readonly fb = inject(FormBuilder);

  providers = toSignal(this.adminProviderContractController.loadAllProvidersWithContractsShort(), {
    initialValue: [],
  });
  providerOptions = computed<DirectusTranslatedItem[]>(() =>
    this.providers().map((contract) => {
      const id = contract.hrToolProvider?.id || '';
      return {
        id,
        translations: [
          {
            id: 0,
            relationship_id: id,
            language: this.transloco.getActiveLang(),
            name: contract.hrToolProvider?.name || '',
          },
        ],
      };
    }),
  );

  private currencies$ = this.directusService.loadCurrencies();
  currencies = toSignal(this.currencies$, {initialValue: []});
  currencyOptions = computed<DirectusTranslatedItem[]>(() =>
    this.currencies().map((currency) => {
      const language = this.transloco.getActiveLang();
      const name =
        currency.translations?.find((translation) => translation.language === language)?.name ||
        currency.isoCode;
      return {
        id: currency.isoCode,
        translations: [{id: 0, relationship_id: currency.isoCode, language, name}],
      };
    }),
  );

  invoiceStatusOptions = computed<DirectusTranslatedItem[]>(() =>
    Object.values(InvoiceStatus).map((status) => ({
      id: status,
      translations: [
        {
          id: 0,
          relationship_id: status,
          language: this.transloco.getActiveLang(),
          name: this.transloco.translate(`selecthorAdmin.provider.invoices.statusValue.${status}`),
        },
      ],
    })),
  );

  visible = model<boolean>(false);
  invoiceSaved = output<ProviderContractInvoiceResponse>();

  invoice = input<ProviderContractInvoiceResponse | null>(null);

  readonly form = this.fb.group({
    invoiceNumber: ['', Validators.required],
    status: ['', Validators.required],
    invoiceDate: [null as Date | null, Validators.required],
    amount: [0, Validators.required],
    dueDate: [null as Date | null, [Validators.required]],
    currency: ['', Validators.required],
    providerDirectusUuid: ['', Validators.required],
    description: ['', Validators.required],
    invoiceFile: [null, Validators.required],
  });

  constructor() {
    effect(() => {
      const data = this.invoice();
      // untracked: patchValue/reset synchronously invoke the PrimeNG selects'
      // writeValue(), which reads their internal modelValue signal. Without
      // untracked, that read is captured as a dependency of this effect, so
      // every user selection (modelValue.set) re-runs the effect and patches
      // the original invoice values back over the user's input.
      untracked(() => {
        if (data) {
          const providerUuid = data.provider?.id || '';
          this.form.patchValue({
            invoiceNumber: data.invoiceNumber || '',
            status: data.status,
            invoiceDate: data.invoiceDate ? new Date(data.invoiceDate) : null,
            dueDate: data.dueDate ? new Date(data.dueDate) : null,
            amount: data.invoiceAmount || 0,
            currency: data.currency || '',
            providerDirectusUuid: providerUuid,
            description: data.description || '',
          });

          this.form.get('providerDirectusUuid')?.disable();
          this.form.get('invoiceFile')?.disable(); // Disable file upload in edit mode
          this.form.get('invoiceFile')?.clearValidators();
        } else {
          this.form.reset({
            amount: 0,
            invoiceFile: null,
          });
          this.form.get('providerDirectusUuid')?.enable();
          this.form.get('invoiceFile')?.enable(); // Enable for new invoices
          this.form.get('invoiceFile')?.setValidators(Validators.required);
        }
        this.form.get('invoiceFile')?.updateValueAndValidity();
      });
    });

    // Effect to reset form when dialog is closed
    effect(() => {
      if (!this.visible()) {
        untracked(() => {
          this.form.reset({
            amount: 0,
            invoiceFile: null,
          });
          this.form.markAsPristine();
          this.form.markAsUntouched();
        });
      }
    });
  }

  selectedProviderPlan = toSignal(
    this.form.get('providerDirectusUuid')!.valueChanges.pipe(
      filter((uuid): uuid is string => !!uuid),
      switchMap((uuid) =>
        this.adminProviderContractController.loadProviderContractByDirectusUuid({
          providerDirectusId: uuid,
        }),
      ),
      map((contract) => {
        return contract?.directusProviderPaymentPlan?.translations?.find(
          (t) => t.language === this.transloco.getActiveLang(),
        )?.name;
      }),
    ),
    {initialValue: null},
  );

  submitting = false;

  get selectedFile(): File | null {
    return this.form.get('invoiceFile')?.value as File | null;
  }

  close(): void {
    this.visible.set(false);
  }

  protected async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const file = this.selectedFile;
    this.submitting = true;

    try {
      // Only read file if one was actually selected
      const fileContent = file ? await this.getFileBase64(file) : '';
      const request = this.convertFormToInvoiceRequest(fileContent);

      let savedInvoiceObservable;
      if (this.invoice()?.invoiceUuid) {
        savedInvoiceObservable = this.adminProviderContractController.updateInvoice({
          invoiceUuid: this.invoice()?.invoiceUuid || '',
          providerContractInvoiceRequest: request.providerContractInvoiceRequest,
        });
      } else {
        savedInvoiceObservable = this.adminProviderContractController.createNewInvoice(request);
      }
      savedInvoiceObservable
        .pipe(
          finalize(() => {
            this.submitting = false;
          }),
        )
        .subscribe({
          next: (invoice) => {
            this.invoiceSaved.emit(invoice);
            this.form.reset();
            this.visible.set(false);
          },
        });
    } catch (error) {
      console.error('Error reading file', error);
      this.submitting = false;
    }
  }

  private async getFileBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // The result looks like "data:application/pdf;base64,JVBERi..."
        // We usually only want the part after the comma
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  private convertFormToInvoiceRequest(fileBase64: string): CreateNewInvoiceRequestParams {
    const rawValue = this.form.getRawValue();

    return {
      providerContractInvoiceRequest: {
        invoiceNumber: rawValue.invoiceNumber ?? '',
        status: rawValue.status as InvoiceStatus,
        invoiceDate: rawValue.invoiceDate ? new Date(rawValue.invoiceDate).toISOString() : '',
        dueDate: rawValue.dueDate ? new Date(rawValue.dueDate).toISOString() : '',
        invoiceAmount: rawValue.amount ?? 0,
        currency: rawValue.currency ?? '',
        contractUuid: this.providers().find(
          (provider) => provider.hrToolProviderDirectusId === rawValue.providerDirectusUuid,
        )?.contractUuid,
        description: rawValue.description ?? '',
        invoiceFileContent: fileBase64,
        type: ProviderInvoiceType.CONTRACT,
      },
    };
  }
}
